import { percentage } from './formatters'

// Keep this deliberate frontend copy aligned with
// ts-bulk-actions/src/bulk-engine/actions/campaign-bid-strategy-map.ts.
export type BidStrategy = 'SALES_DOWN_ONLY' | 'SALES' | 'NONE'

const BID_STRATEGY_LABELS: Record<BidStrategy, string> = {
    SALES_DOWN_ONLY: 'Dynamic bids - down only',
    SALES: 'Dynamic bids - up and down',
    NONE: 'Fixed bids',
}

export const formatBidStrategy = (strategy: string | null | undefined): string | undefined =>
    isBidStrategy(strategy) ? BID_STRATEGY_LABELS[strategy] : undefined

/** Amazon and campaign_placements store placement adjustments as percentage points. */
export const formatPlacementAdjustment = (percentagePoints: number): string =>
    percentage(percentagePoints, 0)

/** Apply a placement adjustment to a bid expressed in cents. */
export const effectiveBidCents = (bidCents: number, percentagePoints: number): number =>
    bidCents * (1 + percentagePoints / 100)

export type PlacementAdjustmentName = 'topOfSearch' | 'restOfSearch' | 'homePage' | 'productPage'

export type EffectiveBidRangeInput = {
    baseBidCents: number | null | undefined
    adGroupDefaultBidCents: number | null | undefined
    strategy: string | null | undefined
    adjustments: Partial<Record<PlacementAdjustmentName, number | null | undefined>>
}

export type EffectiveBidPlacementRange = {
    adjustmentPercent: number
    placementAdjustedBidCents: number
    floorCents: number
    ceilingCents: number
}

export type EffectiveBidRanges = {
    placements: Record<PlacementAdjustmentName, EffectiveBidPlacementRange>
    floorCents: number
    ceilingCents: number
}

const PLACEMENT_NAMES: PlacementAdjustmentName[] = [
    'topOfSearch',
    'restOfSearch',
    'homePage',
    'productPage',
]

const DYNAMIC_MAX_MULTIPLIERS: Record<BidStrategy, number> = {
    SALES_DOWN_ONLY: 1,
    SALES: 2,
    NONE: 1,
}

const isBidStrategy = (value: string | null | undefined): value is BidStrategy =>
    value === 'SALES_DOWN_ONLY' || value === 'SALES' || value === 'NONE'

/**
 * Calculates placement-adjusted effective-bid ranges in integer cents.
 * Every calculated cent amount uses Math.round.
 */
export const calculateEffectiveBidRanges = (
    input: EffectiveBidRangeInput,
): EffectiveBidRanges | null => {
    const baseBidCents = input.baseBidCents ?? input.adGroupDefaultBidCents

    if (!Number.isFinite(baseBidCents) || !isBidStrategy(input.strategy)) {
        return null
    }

    const placements = {} as Record<PlacementAdjustmentName, EffectiveBidPlacementRange>

    for (const placement of PLACEMENT_NAMES) {
        const adjustmentPercent = input.adjustments[placement] ?? 0
        const placementAdjustedBidCents = Math.round(
            effectiveBidCents(baseBidCents, adjustmentPercent),
        )
        const floorCents = input.strategy === 'NONE' ? placementAdjustedBidCents : 0
        // No Amazon source states whether placement adjustment and the dynamic multiplier
        // compound: $1 at +50% under SALES is $3 compounding or $2.50 additive. We implement
        // compounding; if resolved as additive, change this formula and its expected test values.
        const ceilingCents =
            input.strategy === 'NONE'
                ? placementAdjustedBidCents
                : Math.round(
                      placementAdjustedBidCents * DYNAMIC_MAX_MULTIPLIERS[input.strategy],
                  )

        placements[placement] = {
            adjustmentPercent,
            placementAdjustedBidCents,
            floorCents,
            ceilingCents,
        }
    }

    const ranges = Object.values(placements)
    return {
        placements,
        floorCents: Math.min(...ranges.map((range) => range.floorCents)),
        ceilingCents: Math.max(...ranges.map((range) => range.ceilingCents)),
    }
}
